import { ComponentProps, createContext, useContext, MouseEvent, useState } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';

import { CheckIcon, ChevronDownIcon, ChevronUpIcon, CloseIcon } from '@/assets/icons';
import { mergeClasses } from '@/utils';
import { Label } from '@/components/common';

import { ISelectContextValue } from './Select.interfaces';

const SelectContext = createContext<ISelectContextValue>({});

const Select = ({
  className,
  label,
  onValueChange,
  value,
  required,
  error,
  ...props
}: ComponentProps<typeof SelectPrimitive.Root> & {
  className?: string;
  label?: string;
  error?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <SelectContext.Provider value={{ onValueChange, value, error, isOpen }}>
      <div className={mergeClasses('flex flex-col gap-1.5', className)}>
        {label && (
          <Label className="!gap-0 font-semibold">
            {label}
            {required && <span className="ml-1 text-semantic-error-base">*</span>}
          </Label>
        )}
        <SelectPrimitive.Root
          key={value ?? 'empty'}
          data-slot="select"
          value={value}
          onValueChange={onValueChange}
          open={isOpen}
          onOpenChange={setIsOpen}
          required={required}
          {...props}
        />
        {error && <p className="text-xs text-semantic-error-base">{error}</p>}
      </div>
    </SelectContext.Provider>
  );
};

const SelectGroup = ({ ...props }: ComponentProps<typeof SelectPrimitive.Group>) => {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
};

const SelectValue = ({ ...props }: ComponentProps<typeof SelectPrimitive.Value>) => {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
};

const SelectTrigger = ({
  className,
  size = 'default',
  allowClear = false,
  children,
  ...props
}: ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: 'sm' | 'default';
  allowClear?: boolean;
}) => {
  const { onValueChange, value, error, isOpen } = useContext(SelectContext);
  const showClearButton = allowClear && Boolean(value);

  const clearSelection = (e: MouseEvent) => {
    e.stopPropagation();
    onValueChange?.(undefined as any);
  };

  return (
    <div className="relative inline-block w-full">
      <SelectPrimitive.Trigger
        data-slot="select-trigger"
        data-size={size}
        className={mergeClasses(
          "shadow-none rounded-soft border-neutral-n4 data-[placeholder]:text-neutral-n7 hover:border-neutral-n6 data-[placeholder]:hover:text-brand-primary-darkest focus:border-brand-primary-base data-[state=open]:border-brand-primary-base data-[placeholder]:focus:text-neutral-n7 [&_svg:not([class*='text-'])]:text-brand-primary-darkest aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-full items-center gap-2 border bg-transparent pl-3 py-2 text-sm whitespace-nowrap transition-[color,box-shadow] outline-none data-[size=default]:h-10 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
          showClearButton ? 'pr-14' : 'pr-8',
          error && '!border-semantic-error-base',
          props.disabled && '!bg-neutral-n2 !border-neutral-n4 text-neutral-n6 cursor-not-allowed',
          className,
        )}
        {...props}
      >
        {children}
        <SelectPrimitive.Icon asChild>
          {isOpen ? (
            <ChevronUpIcon className="size-4 absolute right-3 text-brand-primary-base disabled:text-neutral-n6" />
          ) : (
            <ChevronDownIcon className="size-4 absolute right-3 disabled:text-neutral-n6" />
          )}
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      {showClearButton && (
        <button
          type="button"
          onClick={clearSelection}
          className="text-neutral-n6 hover:text-neutral-n8 absolute top-1/2 right-9 size-4 -translate-y-1/2 flex items-center justify-center transition-colors z-10"
          aria-label="Clear selection"
        >
          <CloseIcon className="w-base h-base" />
        </button>
      )}
    </div>
  );
};

const SelectContent = ({
  className,
  children,
  position = 'popper',
  align = 'center',
  ...props
}: ComponentProps<typeof SelectPrimitive.Content>) => {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={mergeClasses(
          'max-h-[var(--radix-select-content-available-height)] bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        align={align}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={mergeClasses(
            'py-s !overflow-visible',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
};

const SelectLabel = ({ className, ...props }: ComponentProps<typeof SelectPrimitive.Label>) => {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={mergeClasses('text-muted-foreground px-2 py-1.5 text-xs', className)}
      {...props}
    />
  );
};

const SelectItem = ({
  className,
  children,
  ...props
}: ComponentProps<typeof SelectPrimitive.Item>) => {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={mergeClasses(
        "cursor-pointer outline-none focus:bg-neutral-n2 text-sm size-xl pl-base text-brand-primary-darkest font-medium focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full items-center gap-2 rounded-sm py-1.5 pr-8 select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className,
      )}
      {...props}
    >
      <span
        data-slot="select-item-indicator"
        className="absolute right-2 flex size-3.5 items-center justify-center"
      >
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
};

const SelectSeparator = ({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.Separator>) => {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={mergeClasses('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
};

const SelectScrollUpButton = ({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.ScrollUpButton>) => {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={mergeClasses('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
};

const SelectScrollDownButton = ({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.ScrollDownButton>) => {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={mergeClasses('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
};

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
